import { withScope } from '@sentry/react';
import axios, { AxiosError } from 'axios';
import axiosRetry from 'axios-retry';
import { clone } from 'lodash';
import { AxiosHttpClient, apiInstance, queryParamsSerializer } from '@commutifi-fe/commutifi-specific/api/axios';
import { CANCELED_API_REQ, LocalesType, getSupportedLocale } from '@commutifi-fe/constants';
import { CommutifiErrorData } from '@commutifi-fe/interfaces';
import { DEFAULT_LOCALE } from 'constants/settings';
import config from 'config/index';
import { destroyMessage, displayErrorRetryLoading, displayFailedRetryError } from 'shared/components/common/RecurringError';
import { captureError } from 'utils/helpers/errors';
import sessionStorage from 'utils/sessionStorage';
import { logger } from '@commutifi-fe/logger';
import storage from 'utils/storage';
import { ACCOUNT_ID_IDENTIFIER, CSRF_COOKIE_NAME, CSRF_HEADER_NAME, REQUEST_ID_HEADER } from './constants';
export { ACCOUNT_ID_IDENTIFIER, JSON_HEADER, cancelMessage } from './constants';

/**
 * Generate a UUID v4 for request correlation
 * Uses crypto.randomUUID() when available (modern browsers), falls back to manual generation
 */
const generateRequestId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
};

// To support old name
export { apiInstance as apiCall };
export const {
  CancelToken
} = axios;
export const AxiosHttpInstance = new AxiosHttpClient({
  withCredentials: true,
  baseURL: config.dashboardApiUrl,
  headers: {
    'Accept-Language': DEFAULT_LOCALE
  },
  // NOTE: this is 1 hour, not the "1 minute" a previous comment here claimed. Effectively no
  // timeout — a hung request never surfaces. Lowering it changes behaviour for every call in the
  // app (long-running exports included), so it needs its own change with QA rather than riding
  // along here. Callers that must fail fast pass their own `timeout`.
  timeout: 60 * 60 * 1000,
  paramsSerializer: queryParamsSerializer
});
export const dashboardApiServer = AxiosHttpInstance.getClient();

/**
 * CSRF Protection: Get CSRF token from cookie.
 * Exported for use by non-axios requests (e.g., Ant Design Upload).
 */
export const getCsrfToken = (): string | null => {
  const match = document.cookie.match(new RegExp(`${CSRF_COOKIE_NAME}=([^;]+)`));
  return match ? match[1] : null;
};
dashboardApiServer.interceptors.request.use(config => {
  // Add request ID for distributed tracing correlation
  // This ID is generated on frontend and propagated to backend for log correlation
  // Only generate if not already set - preserves ID across axios-retry attempts
  if (!config.headers[REQUEST_ID_HEADER]) {
    config.headers[REQUEST_ID_HEADER] = generateRequestId();
  }

  // Add CSRF token to state-changing requests
  if (config.method && !['get', 'head', 'options'].includes(config.method.toLowerCase())) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers[CSRF_HEADER_NAME] = csrfToken;
    }
  }
  return config;
}, undefined,
// No error handler
{
  // CRITICAL: Enable synchronous mode to fix axios bug where error interceptor
  // return values are ignored in async mode. See:
  // https://github.com/axios/axios/issues/5784
  // In async mode, axios does: .catch(handler).then(() => prevResult) which discards
  // the handler's return value. Sync mode uses: .then().catch() which correctly propagates.
  synchronous: true
});

/**
 * Setup axios retry
 */
if (process.env.NODE_ENV !== 'test') {
  axiosRetry(dashboardApiServer, {
    retries: 3,
    retryDelay: (retryCount, error) => {
      displayErrorRetryLoading(error.config?.url || `${retryCount}`, retryCount);
      return Math.min(1000 * 2 ** retryCount, 30000);
    },
    // Skipping 500s as they are legit errors that we most likely can't recuperate from
    retryCondition: error => Boolean(error.response?.status && error.response.status > 500)
  });
}

// NOTE: ARC integration is deprecated
// arcSubscriptionKey has been removed from config for security (was leaking secret to frontend)
export const arcApiServer = axios.create({
  baseURL: config.arcApiUrl
  // Subscription key intentionally removed - this integration is no longer used
});

/**
 * We use the session storage when the user don't want to stay logged in so when the user closes the tab is session will be ended.
 * In all other cases we want to persist the user session so we store the id in the local storage
 */
export const saveAuthenticated = (accountId: string, options: {
  stayLoggedIn?: boolean;
} = {}) => {
  if (options.stayLoggedIn === false) {
    sessionStorage.put(ACCOUNT_ID_IDENTIFIER, accountId);
  } else {
    storage.put(ACCOUNT_ID_IDENTIFIER, accountId);
  }
};

/**
 * Tries to access the local storage to see if we have the account id stored. That means we can assume the user
 * is logged in with our backend.
 * We need to fallback to the session storage cause we can have users that chose the option to NOT stay logged in.
 * In that case we use the local storage so when the user closes the tab is session will be ended
 */
export const getAuthenticatedAccountId = () => storage.get(ACCOUNT_ID_IDENTIFIER) || sessionStorage.get(ACCOUNT_ID_IDENTIFIER);
export const clearAuthenticated = () => {
  storage.remove(ACCOUNT_ID_IDENTIFIER);
  // `saveAuthenticated` writes here instead when the user unticked "keep me signed in", and
  // `getAuthenticatedAccountId` reads both — so skipping this leaves a logged-out user still
  // looking authenticated to every caller of that gate.
  sessionStorage.remove(ACCOUNT_ID_IDENTIFIER);
  document.cookie.split(';').forEach(c => {
    document.cookie = c.replace(/^ +/, '').replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
  });
};
export const setLocaleHeader = (locale: LocalesType | `${LocalesType}`) => {
  dashboardApiServer.defaults.headers['Accept-Language'] = getSupportedLocale(locale);
};
export const setHeader = (key: string, value: any) => {
  dashboardApiServer.defaults.headers[key] = value;
};
export const getHeader = (key: string) => dashboardApiServer.defaults.headers[key];
const handleErrorCapture = (error: AxiosError<CommutifiErrorData>) => {
  try {
    // Extract request ID for correlation with backend logs
    const requestId = error.config?.headers?.[REQUEST_ID_HEADER] as string | undefined;

    // Skip 401 errors - they're expected authentication errors handled by interceptors
    // (session expired, token refresh, redirect to login). No need to track in Sentry.
    const status = error.response?.status ?? error.request?.status;
    if (status === 401) {
      return;
    }
    if (error.response) {
      withScope(scope => {
        scope.setTag('HTTP', error.config?.method);
        const endpoint = error.response?.config.url ? error.response.config.url.split('?')[0] : undefined;
        if (endpoint) scope.setTag('Endpoint', endpoint);
        if (requestId) scope.setTag('request_id', requestId);

        // Group API errors by method + endpoint + status code
        // This groups all "GET /api/wallets 500" together regardless of which component called it
        const errorFingerprint = endpoint ? [error.response?.config.method || '', endpoint, String(error.response?.config.data?.code ?? error.response?.status)] : undefined;
        if (errorFingerprint) {
          scope.setFingerprint(errorFingerprint);
        }

        // Capture error on Sentry with request context
        captureError(error, {
          extra: {
            url: error.response?.config.url,
            data: error.response?.config.data,
            status: error.response?.status,
            httpCode: error.response?.config.data?.httpCode,
            commutifiCode: error.response?.data.code,
            error: error.request.response || error.request.statusText,
            response: error.request.responseText,
            request_id: requestId
          }
        });
      });
    }
    if (error.request) {
      withScope(scope => {
        scope.setTag('HTTP', 'networkError');
        if (requestId) scope.setTag('request_id', requestId);
        // Group all network errors together - they're usually connectivity issues, not code bugs
        // Individual endpoints are still visible in the extra data for filtering
        scope.setFingerprint(['network-error']);
        captureError(error, {
          extra: {
            url: error.request.responseURL,
            status: error.request.status,
            response: error.request.response,
            error: error.request.response || error.request.statusText,
            request_id: requestId
          }
        });
      });
    }
  } catch (e) {
    logger.warning('Sentry is probably not loaded. Error: ', {
      error: e
    });
  }
};
export const apiDashboardServer = apiInstance(dashboardApiServer, {
  onSuccess: res => {
    if (res.config?.['axios-retry']?.retryCount && res.config['axios-retry'].retryCount <= 3) {
      res.config.url && destroyMessage(res.config.url);
    }
  },
  onError: error => {
    handleErrorCapture(error);
    if (error.config?.['axios-retry']?.retryCount && error.config['axios-retry'].retryCount >= 3) {
      displayFailedRetryError(error);
    }
    if (axios.isCancel(clone(error))) {
      logger.log('Request canceled: ', {
        message: error.message
      });
      return CANCELED_API_REQ;
    }
    return undefined;
  }
});